import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { getFirestore, collection } from 'firebase/firestore';
import { normalizeCategoryGid } from '../constants/shopifyCategories';

// Location ID to Name mapping for Chorost stores
const CHOROST_LOCATIONS = {
  '80788062451': 'Carlsbad',
  '71951548659': 'Grant',
  '71974158579': 'Green Valley',
  '71951614195': 'Indio',
  '85030797555': 'La Jolla',
  '85030895859': 'Mission Valley',
  '34167685200': 'Main Office',
  '67967746291': 'Oracle',
  '71951581427': 'Palm Desert',
  '70154354931': 'Palm Springs',
  '71951646963': 'Phoenix',
  '71951483123': 'Rita Ranch',
  '70273335539': 'Scottsdale',
  '71951450355': 'Speedway',
  '71951515891': 'Tangerine',
  '71951417587': 'Tanque Verde',
  '85031060659': 'Tempe',
  '91600486643': 'Sierra Vista',
  '94260035827': 'Chorost & Co. Store',
} as const;

/** Shopify POS location "Chorost & Co. Store". Keep in sync with salesService. */
export const CHOROST_STORE_LOCATION_ID = '94260035827';

interface ShopifyProductResponse {
  product: {
    id: string;
    title: string;
    variants: Array<{
      id: string;
      inventory_quantity: number;
      sku: string;
      barcode?: string;
    }>;
  };
}

interface ShopifyProduct {
  title: string;
  body_html: string;
  vendor: string;
  product_type: string;
  status: 'active' | 'draft';
  tags: string[];
  variants: Array<{
    price: string;
    sku: string;
    barcode?: string;
    inventory_quantity: number;
    inventory_management: 'shopify';
  }>;
  metafields: Array<{
    key: string;
    value: string;
    type: string;
    namespace: string;
  }>;
}

export interface ShopifyField {
  id: string;
  name: string;
  type: 'text' | 'number' | 'boolean' | 'select' | 'multiselect' | 'date' | 'json';
  required: boolean;
  options?: string[];
  defaultValue?: any;
  description?: string;
  metafield?: {
    namespace: string;
    key: string;
    type: string;
  };
}

export interface ShopifyType {
  id: string;
  name: string;
  fields: ShopifyField[];
  tags: string[];
  collections: string[];
}

export interface ShopifyLocation {
  id: string;
  name: string;
  address1?: string;
  city?: string;
  province?: string;
  country?: string;
  zip?: string;
  phone?: string;
  created_at: string;
  updated_at: string;
  country_code?: string;
  country_name?: string;
  province_code?: string;
  legacy?: boolean;
  active?: boolean;
}

export interface ShopifyPOSTransaction {
  id: string;
  order_id?: string;
  amount: string;
  currency: string;
  kind: string; // 'sale', 'refund', 'void'
  gateway: string;
  status: string;
  message?: string;
  created_at: string;
  test: boolean;
  authorization?: string;
  location_id?: string;
  source_name?: string;
  receipt?: {
    charges?: any[];
  };
}

export interface ShopifyOrder {
  id: string;
  order_number: string;
  created_at: string;
  updated_at: string;
  total_price: string;
  subtotal_price: string;
  total_tax: string;
  currency: string;
  financial_status: string;
  fulfillment_status?: string;
  line_items: Array<{
    id: string;
    title: string;
    quantity: number;
    price: string;
    total_discount: string;
    sku?: string;
    product_id?: string;
    variant_id?: string;
  }>;
  customer?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
  };
  location_id?: string;
  pos?: boolean;
  source_name?: string;
  tags?: string;
}

export interface LocationSalesData {
  locationId: string;
  locationName: string;
  totalSales: number;
  transactionCount: number;
  averageTicket: number;
  previousMonthSales: number;
  growthPercent: number;
  status: 'online' | 'offline';
}

export class ShopifyService {
  private baseUrl = 'https://us-central1-chorost-store-center.cloudfunctions.net/shopifyProxy';
  private headers = {
    'Content-Type': 'application/json'
  };

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const requestOptions: RequestInit = {
      ...options,
      headers: {
        ...this.headers,
        ...options.headers,
      },
      mode: 'cors'
    };

    console.log('Making request to:', `${this.baseUrl}${endpoint}`);
    console.log('Request options:', JSON.stringify(requestOptions, null, 2));

    const response = await fetch(`${this.baseUrl}${endpoint}`, requestOptions);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
      console.error('Shopify API error response:', {
        status: response.status,
        statusText: response.statusText,
        errorData
      });
      
      // For 422 errors, show detailed validation errors
      if (response.status === 422 && errorData.errors) {
        const errorMessages = Object.entries(errorData.errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ');
        throw new Error(`Validation error: ${errorMessages}`);
      }
      
      // For 500 errors, provide more context
      if (response.status === 500) {
        console.error('Shopify 500 Error - Full error data:', JSON.stringify(errorData, null, 2));
        const detailedError = errorData.errors 
          ? `Server error: ${JSON.stringify(errorData.errors)}` 
          : errorData.message || 'Internal server error from Shopify';
        throw new Error(`Shopify server error (500): ${detailedError}`);
      }
      
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  async createProduct(productData: any, categoryGid?: string) {
    console.log('Creating product with data:', JSON.stringify(productData, null, 2));
    if (categoryGid) {
      console.log('🏷️  Category GID to set after creation:', categoryGid);
    }
    
    try {
      // Step 1: Create the product via REST API
      const response = await this.request<ShopifyProductResponse>('/products.json', {
        method: 'POST',
        body: JSON.stringify({ product: productData })
      });
      
      console.log('Product created successfully:', response);
      
      if (response?.product?.id) {
        const productId = response.product.id;
        const variantId = response.product.variants?.[0]?.id;
        
        console.log('📍 Setting up product category, sales channels, and inventory for product:', productId);
        
        // Step 1.5: Set product category via GraphQL (REST API doesn't support this)
        if (categoryGid) {
          try {
            console.log('🏷️  Setting category via GraphQL for product:', productId);
            await this.setProductCategory(productId, categoryGid);
            console.log('✅ Product category set successfully to:', categoryGid);
          } catch (error) {
            console.error('❌ Failed to set product category:', error);
            // Don't fail the entire product creation if category setting fails
          }
        } else {
          console.log('⚠️  No category_gid provided, skipping category assignment');
        }
        
        // Step 2: Publish to all sales channels
        try {
          await this.publishProductToAllChannels(productId);
          console.log('✅ Product published to all sales channels');
          
          // Verify the publications were successful
          await this.verifyProductPublications(productId);
        } catch (error) {
          console.error('❌ Failed to publish to all sales channels:', error);
          // Don't throw the error to prevent product creation from failing
          // but log it more prominently for debugging
          
          // Still try to verify what channels it's published to
          await this.verifyProductPublications(productId);
        }
        
        // Step 3: Set up inventory at all locations (if variant exists)
        if (variantId && response.product.variants?.[0]?.inventory_quantity > 0) {
          try {
            await this.setupInventoryAtAllLocations(variantId, response.product.variants[0].inventory_quantity);
            console.log('✅ Inventory set up at all locations');
          } catch (error) {
            console.warn('⚠️ Failed to set up inventory at all locations:', error);
          }
        }
      }
      
      return response;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  }

  async addProductImage(productId: string, imageUrl: string, position: number = 0): Promise<void> {
    try {
      console.log(`📸 Adding image to product ${productId}`);
      console.log(`   Position: ${position + 1}`);
      console.log(`   Image URL: ${imageUrl}`);
      
      // Verify the image URL is accessible
      try {
        const testFetch = await fetch(imageUrl, { method: 'HEAD' });
        console.log(`   Image accessibility check: ${testFetch.ok ? '✅ Accessible' : '❌ Not accessible'}`);
        console.log(`   Status: ${testFetch.status}`);
      } catch (fetchError: any) {
        console.error(`   ⚠️ Image URL may not be publicly accessible:`, fetchError.message);
      }
      
      const response = await this.request(`/products/${productId}/images.json`, {
        method: 'POST',
        body: JSON.stringify({
          image: {
            src: imageUrl,
            position: position + 1 // Shopify positions start at 1
          }
        })
      });
      
      console.log('   ✅ Image added successfully to Shopify');
      console.log('   Response:', response);
      return response;
    } catch (error: any) {
      console.error('   ❌ Error adding product image:', error);
      console.error('   Error details:', {
        message: error.message,
        status: error.status,
        body: error.body
      });
      throw new Error(`Failed to add image: ${error.message || 'Unknown error'}`);
    }
  }

  async publishProductToAllChannels(productId: string): Promise<void> {
    console.log('📢 Publishing product to ALL sales channels (forced):', productId);
    
    try {
      // ALWAYS use the aggressive method to ensure ALL channels
      console.log('🚀 Using forced publishing method to guarantee ALL channels...');
      await this.ensureProductIsPublished(productId);
      console.log('✅ Forced publishing complete - product should be on ALL sales channels');
    } catch (error) {
      console.error('❌ Error in publishProductToAllChannels:', error);
      console.warn('⚠️ Product created but publishing may have failed. You can manually publish in Shopify Admin.');
    }
  }

  async ensureProductIsPublished(productId: string): Promise<void> {
    console.log('🔄 Using GraphQL to publish product to ALL sales channels:', productId);
    
    try {
      // Step 1: Get all available publications (sales channels)
      const publicationsQuery = `
        query {
          publications(first: 50) {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      `;
      
      const pubResponse = await this.request<any>('/graphql.json', {
        method: 'POST',
        body: JSON.stringify({ query: publicationsQuery })
      });
      
      const publications = pubResponse?.data?.publications?.edges || [];
      console.log(`📋 Found ${publications.length} sales channels:`, publications.map((p: any) => p.node.name));
      
      if (publications.length === 0) {
        console.warn('⚠️ No publications found - product may only be available in admin');
        return;
      }
      
      // Step 2: Convert numeric product ID to GraphQL GID
      const productGid = productId.startsWith('gid://') 
        ? productId 
        : `gid://shopify/Product/${productId}`;
      
      // Step 3: Prepare publication inputs for ALL channels
      const publicationInputs = publications.map((pub: any) => ({
        publicationId: pub.node.id
      }));
      
      console.log('📢 Publishing to ALL channels via GraphQL publishablePublish mutation...');
      
      // Step 4: Use GraphQL publishablePublish mutation to publish to ALL channels at once
      const publishMutation = `
        mutation publishToAllChannels($id: ID!, $input: [PublicationInput!]!) {
          publishablePublish(id: $id, input: $input) {
            publishable {
              availablePublicationsCount {
                count
              }
              publicationCount
            }
            shop {
              id
            }
            userErrors {
              field
              message
            }
          }
        }
      `;
      
      const publishResponse = await this.request<any>('/graphql.json', {
        method: 'POST',
        body: JSON.stringify({
          query: publishMutation,
          variables: {
            id: productGid,
            input: publicationInputs
          }
        })
      });
      
      // Check for errors
      if (publishResponse?.data?.publishablePublish?.userErrors?.length > 0) {
        const errors = publishResponse.data.publishablePublish.userErrors;
        console.error('❌ GraphQL errors publishing to channels:', errors);
        throw new Error(`Failed to publish: ${errors.map((e: any) => e.message).join(', ')}`);
      }
      
      const publishResult = publishResponse?.data?.publishablePublish;
      console.log('✅ Product successfully published to ALL channels via GraphQL!', {
        availablePublicationsCount: publishResult?.publishable?.availablePublicationsCount?.count,
        publicationCount: publishResult?.publishable?.publicationCount
      });
      
    } catch (error: any) {
      console.error('❌ Failed to publish using GraphQL method:', error);
      console.warn('⚠️ Falling back to REST API method...');
      
      // Fallback: Try REST API method
      try {
        await this.request(`/products/${productId}.json`, {
          method: 'PUT',
          body: JSON.stringify({
            product: {
              id: productId,
              // Intentionally omit status so we preserve whatever the product was
              // created with (jewelry = active, watches = draft).
              published: true,
              published_scope: 'global'
            }
          })
        });
        console.log('✅ Fallback: Product published globally via REST (status preserved)');
      } catch (fallbackError) {
        console.error('❌ Fallback also failed:', fallbackError);
        throw fallbackError;
      }
    }
  }

  async setProductCategory(productId: string, categoryGid: string): Promise<void> {
    const normalizedGid = normalizeCategoryGid(categoryGid) || categoryGid;
    console.log('🏷️  Setting product category via GraphQL:', { productId, categoryGid: normalizedGid });
    
    try {
      // Convert numeric product ID to GraphQL GID if needed
      const productGid = productId.toString().startsWith('gid://') 
        ? productId 
        : `gid://shopify/Product/${productId}`;
      
      console.log('🔄 Converted product GID:', productGid);
      
      // GraphQL mutation to update product category
      // NOTE: According to Shopify Admin API 2024-01+, the category field expects
      // a full GID string in the format: "gid://shopify/TaxonomyCategory/aa-1-12"
      const mutation = `
        mutation updateProductCategory($input: ProductInput!) {
          productUpdate(input: $input) {
            product {
              id
              category {
                id
                name
                fullName
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `;
      
      // IMPORTANT: The category field in Shopify GraphQL expects just the GID string
      // NOT an object with an id property
      const variables = {
        input: {
          id: productGid,
          category: normalizedGid  // gid://shopify/TaxonomyCategory/aa-6-*
        }
      };
      
      console.log('🔍 DEBUG - Category being set:');
      console.log('   Product GID:', productGid);
      console.log('   Category GID:', categoryGid);
      console.log('   Full variables:', JSON.stringify(variables, null, 2));
      
      const response = await this.request<any>('/graphql.json', {
        method: 'POST',
        body: JSON.stringify({
          query: mutation,
          variables: variables
        })
      });
      
      console.log('📥 GraphQL response:', JSON.stringify(response, null, 2));
      
      // Check for errors
      if (response?.data?.productUpdate?.userErrors?.length > 0) {
        const errors = response.data.productUpdate.userErrors;
        console.error('❌ GraphQL errors setting category:', errors);
        console.error('❌ Attempted to set category GID:', categoryGid);
        console.error('❌ On product GID:', productGid);
        throw new Error(`Failed to set category: ${errors.map((e: any) => e.message).join(', ')}`);
      }
      
      const productCategory = response?.data?.productUpdate?.product?.category;
      const updatedCategoryGid = productCategory?.id;
      const updatedCategoryName = productCategory?.name;
      const updatedCategoryFullName = productCategory?.fullName;
      
      console.log('✅ Product category updated successfully');
      console.log('   📋 Requested category GID:', categoryGid);
      console.log('   ✔️  Actual category GID:', updatedCategoryGid);
      console.log('   📛 Category name:', updatedCategoryName);
      console.log('   📂 Category full path:', updatedCategoryFullName);
      
      // VERIFY the category was actually set correctly
      if (updatedCategoryGid !== categoryGid) {
        console.error('🚨 CRITICAL: Category mismatch detected!');
        console.error('   ❌ Expected:', categoryGid);
        console.error('   ❌ Got:', updatedCategoryGid);
        console.error('   ❌ Category name:', updatedCategoryFullName);
        console.error('   🔍 This means Shopify set a DIFFERENT category than requested!');
        console.error('   💡 Possible causes:');
        console.error('      1. Invalid category GID');
        console.error('      2. Category not available for this product type');
        console.error('      3. Shopify taxonomy has changed');
        throw new Error(`Category mismatch: Expected ${categoryGid} but got ${updatedCategoryGid} (${updatedCategoryFullName})`);
      }
      
    } catch (error: any) {
      console.error('❌ Failed to set product category:', error);
      console.error('❌ Error details:', {
        message: error?.message,
        stack: error?.stack
      });
      throw error;
    }
  }

  async verifyProductPublications(productId: string): Promise<void> {
    console.log('🔍 Verifying product publications for:', productId);
    
    try {
      // Get all publications for this product
      const productPublications = await this.request<{ publications: Array<{ id: string; name: string; published_at: string }> }>(`/products/${productId}/publications.json`);
      
      if (productPublications?.publications?.length > 0) {
        console.log('📋 Product is published to:');
        productPublications.publications.forEach(pub => {
          console.log(`  ✅ ${pub.name} (published at: ${pub.published_at})`);
        });
      } else {
        console.warn('⚠️ Product is not published to any sales channels');
      }
    } catch (error) {
      console.error('❌ Error verifying product publications:', error);
    }
  }

  async getAllSalesChannels(): Promise<Array<{ id: string; name: string }>> {
    console.log('📋 Getting all available sales channels...');
    
    try {
      const publications = await this.request<{ publications: Array<{ id: string; name: string }> }>('/publications.json');
      
      if (publications?.publications?.length > 0) {
        console.log('📋 Available sales channels:');
        publications.publications.forEach(pub => {
          console.log(`  📺 ${pub.name} (ID: ${pub.id})`);
        });
        return publications.publications;
      } else {
        console.warn('⚠️ No sales channels found');
        return [];
      }
    } catch (error) {
      console.error('❌ Error getting sales channels:', error);
      return [];
    }
  }

  async setupInventoryAtAllLocations(variantId: string, quantity: number): Promise<void> {
    console.log('📦 Setting up inventory at all locations for variant:', variantId, 'with quantity:', quantity);
    
    try {
      // Get all locations
      const locations = await this.getLocations();
      
      if (locations.length > 0) {
        // Set inventory at each location
        const inventoryPromises = locations.map(async (location) => {
          try {
            // First check if inventory level exists for this location
            const inventoryLevels = await this.request<{ inventory_levels: Array<{ location_id: string; available: number }> }>(`/inventory_levels.json?inventory_item_ids=${variantId}&location_ids=${location.id}`);
            
            if (inventoryLevels?.inventory_levels?.length > 0) {
              // Update existing inventory level
              await this.request(`/inventory_levels/set.json`, {
                method: 'POST',
                body: JSON.stringify({
                  location_id: location.id,
                  inventory_item_id: variantId,
                  available: quantity
                })
              });
              console.log(`✅ Set inventory at ${location.name}: ${quantity} units`);
            } else {
              // Connect inventory item to location first, then set quantity
              await this.request(`/inventory_levels/connect.json`, {
                method: 'POST',
                body: JSON.stringify({
                  location_id: location.id,
                  inventory_item_id: variantId
                })
              });
              
              // Then set the quantity
              await this.request(`/inventory_levels/set.json`, {
                method: 'POST',
                body: JSON.stringify({
                  location_id: location.id,
                  inventory_item_id: variantId,
                  available: quantity
                })
              });
              console.log(`✅ Connected and set inventory at ${location.name}: ${quantity} units`);
            }
          } catch (error) {
            console.warn(`⚠️ Failed to set inventory at ${location.name}:`, error);
          }
        });
        
        await Promise.all(inventoryPromises);
      } else {
        console.warn('⚠️ No locations found');
      }
    } catch (error) {
      console.error('❌ Error setting up inventory at all locations:', error);
      throw error;
    }
  }

  async setInventoryAtLocation(inventoryItemId: string, locationId: string, quantity: number): Promise<void> {
    try {
      await this.request('/inventory_levels/set.json', {
        method: 'POST',
        body: JSON.stringify({
          location_id: locationId,
          inventory_item_id: inventoryItemId,
          available: quantity
        })
      });
      console.log(`✅ Set inventory at location ${locationId}: ${quantity} units`);
    } catch (error) {
      console.error(`❌ Failed to set inventory at location ${locationId}:`, error);
      throw error;
    }
  }

  /**
   * Set inventory to 0 for a product by SKU across all locations
   */
  async setInventoryToZeroBySku(sku: string): Promise<void> {
    // Search for product by SKU using GraphQL
    const graphqlQuery = `
      query {
        productVariants(first: 1, query: "sku:${sku}") {
          edges {
            node {
              id
              sku
              inventoryItem {
                id
              }
            }
          }
        }
      }
    `;

    const response = await this.request<any>('/graphql.json', {
      method: 'POST',
      body: JSON.stringify({ query: graphqlQuery })
    });
    
    if (!response?.data?.productVariants?.edges?.length) {
      throw new Error(`Product not found with SKU: ${sku}`);
    }

    const variant = response.data.productVariants.edges[0].node;
    const inventoryItemId = variant.inventoryItem.id.split('/').pop();
    
    // Get all locations
    const locations = await this.getLocations();
    
    // Set inventory to 0 at all locations
    for (const location of locations) {
      await this.setInventoryAtLocation(inventoryItemId, location.id, 0);
    }
  }

  /**
   * Set product metafields using GraphQL metafieldsSet mutation (Recommended approach)
   * Can set up to 25 metafields in a single call
   */
  async setProductMetafields(
    productId: string, 
    metafields: Array<{ namespace: string; key: string; value: string; type: string }>
  ): Promise<void> {
    console.log('📝 Setting product metafields via GraphQL metafieldsSet');
    console.log('📥 Received metafields:', metafields);
    
    try {
      // Convert numeric product ID to GraphQL GID if needed
      const productGid = productId.toString().startsWith('gid://') 
        ? productId 
        : `gid://shopify/Product/${productId}`;
      
      console.log('🔑 Product GID:', productGid);
      
      // Type mapping to ensure Shopify compatibility
      const TYPE_MAP: Record<string, string> = {
        'single_line_text_field': 'single_line_text_field',
        'multi_line_text_field': 'multi_line_text_field',
        'number_integer': 'number_integer',
        'number_decimal': 'number_decimal',
        'boolean': 'boolean'
      };
      
      // Normalize value based on type
      const normalizeValue = (type: string, value: any): string => {
        if (value === undefined || value === null || value === '') return '';
        
        switch (type) {
          case 'boolean':
            // Shopify expects "true"/"false" as strings
            return value === true || value === 'true' || value === 'True' ? 'true' : 'false';
          case 'number_integer':
            const intVal = parseInt(String(value), 10);
            return isNaN(intVal) ? '' : String(intVal);
          case 'number_decimal':
            const floatVal = parseFloat(String(value));
            return isNaN(floatVal) ? '' : String(floatVal);
          default:
            return String(value).trim();
        }
      };
      
      // Filter and prepare metafields for GraphQL mutation (max 25 per call)
      const metafieldsInput = metafields
        .filter(mf => {
          // Only include metafields with valid namespace, key, and non-empty value
          const hasValidStructure = mf && mf.namespace && mf.key;
          const normalizedValue = normalizeValue(mf.type, mf.value);
          const hasValue = normalizedValue !== '';
          
          if (!hasValidStructure) {
            console.warn(`⚠️  Skipping metafield with invalid structure:`, mf);
            return false;
          }
          
          if (!hasValue) {
            console.log(`ℹ️  Skipping empty metafield: ${mf.namespace}.${mf.key}`);
            return false;
          }
          
          return true;
        })
        .map(mf => {
          const shopifyType = TYPE_MAP[mf.type] || mf.type;
          const normalizedValue = normalizeValue(shopifyType, mf.value);
          
          console.log(`✅ Preparing: ${mf.namespace}.${mf.key} [${shopifyType}] = "${normalizedValue}"`);
          
          return {
            ownerId: productGid,
            namespace: mf.namespace,
            key: mf.key,
            type: shopifyType,
            value: normalizedValue
          };
        });
      
      if (metafieldsInput.length === 0) {
        console.warn('⚠️  No valid metafields to send after filtering');
        return;
      }
      
      console.log('📤 Sending metafields to Shopify:', metafieldsInput);
      
      // GraphQL mutation for setting metafields
      const mutation = `
        mutation metafieldsSet($metafields: [MetafieldsSetInput!]!) {
          metafieldsSet(metafields: $metafields) {
            metafields {
              id
              namespace
              key
              type
              value
            }
            userErrors {
              field
              message
              code
            }
          }
        }
      `;
      
      const variables = {
        metafields: metafieldsInput
      };
      
      const response = await this.request<any>('/graphql.json', {
        method: 'POST',
        body: JSON.stringify({
          query: mutation,
          variables: variables
        })
      });
      
      console.log('📥 Metafields response:', JSON.stringify(response, null, 2));
      
      // Check for errors
      if (response?.data?.metafieldsSet?.userErrors?.length > 0) {
        const errors = response.data.metafieldsSet.userErrors;
        console.error('❌ GraphQL errors setting metafields:', errors);
        throw new Error(`Failed to set metafields: ${errors.map((e: any) => `${e.field}: ${e.message}`).join(', ')}`);
      }
      
      const setMetafields = response?.data?.metafieldsSet?.metafields || [];
      console.log(`✅ Successfully set ${setMetafields.length} metafields`);
      setMetafields.forEach((mf: any) => {
        console.log(`   ✔️  ${mf.namespace}.${mf.key} = ${mf.value} (${mf.type})`);
      });
      
    } catch (error: any) {
      console.error('❌ Failed to set product metafields:', error);
      throw error;
    }
  }

  /**
   * Create a metafield definition (run once per metafield type)
   * This makes the field visible in Shopify Admin and enforces validation
   */
  async createMetafieldDefinition(definition: {
    name: string;
    namespace: string;
    key: string;
    type: string;
    ownerType?: string;
    description?: string;
  }): Promise<void> {
    console.log(`📋 Creating metafield definition: ${definition.namespace}.${definition.key}`);
    
    try {
      const mutation = `
        mutation metafieldDefinitionCreate($def: MetafieldDefinitionCreateInput!) {
          metafieldDefinitionCreate(definition: $def) {
            metafieldDefinition {
              id
              name
              namespace
              key
              type {
                name
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `;
      
      const variables = {
        def: {
          name: definition.name,
          namespace: definition.namespace,
          key: definition.key,
          type: definition.type,
          ownerType: definition.ownerType || 'PRODUCT',
          description: definition.description || ''
        }
      };
      
      const response = await this.request<any>('/graphql.json', {
        method: 'POST',
        body: JSON.stringify({
          query: mutation,
          variables: variables
        })
      });
      
      if (response?.data?.metafieldDefinitionCreate?.userErrors?.length > 0) {
        const errors = response.data.metafieldDefinitionCreate.userErrors;
        // If definition already exists, that's okay
        if (errors.some((e: any) => e.message?.includes('already exists'))) {
          console.log(`ℹ️  Metafield definition ${definition.namespace}.${definition.key} already exists`);
          return;
        }
        console.error('❌ Error creating metafield definition:', errors);
        throw new Error(`Failed to create definition: ${errors.map((e: any) => e.message).join(', ')}`);
      }
      
      console.log(`✅ Created metafield definition: ${definition.namespace}.${definition.key}`);
      
    } catch (error: any) {
      // If it's an "already exists" error, that's fine
      if (error.message?.includes('already exists')) {
        console.log(`ℹ️  Metafield definition already exists: ${definition.namespace}.${definition.key}`);
        return;
      }
      console.error('❌ Failed to create metafield definition:', error);
      throw error;
    }
  }

  /**
   * Ensure all required metafield definitions exist (call once on app initialization)
   */
  async ensureMetafieldDefinitions(
    extraKeys?: Array<{ namespace: string; key: string; type?: string }>
  ): Promise<void> {
    console.log('🔧 Ensuring metafield definitions exist...');
    
    const definitions = [
      // Shopify standard taxonomy fields
      {
        name: 'Color',
        namespace: 'shopify',
        key: 'color-pattern',
        type: 'single_line_text_field',
        description: 'Product color or pattern'
      },
      {
        name: 'Age Group',
        namespace: 'shopify',
        key: 'age-group',
        type: 'single_line_text_field',
        description: 'Target age group (Adult, Child, etc.)'
      },
      {
        name: 'Jewelry Type',
        namespace: 'shopify',
        key: 'jewelry-type',
        type: 'single_line_text_field',
        description: 'Type of jewelry item'
      },
      {
        name: 'Target Gender',
        namespace: 'shopify',
        key: 'target-gender',
        type: 'single_line_text_field',
        description: 'Target gender (Unisex, Men, Women, etc.)'
      },
      {
        name: 'Jewelry Material',
        namespace: 'shopify',
        key: 'jewelry-material',
        type: 'single_line_text_field',
        description: 'Primary material of the jewelry'
      },
      // Custom fields
      {
        name: 'Style',
        namespace: 'custom',
        key: 'style',
        type: 'single_line_text_field',
        description: 'Style or design aesthetic'
      },
      {
        name: 'Type',
        namespace: 'custom',
        key: 'type',
        type: 'single_line_text_field',
        description: 'Specific type classification'
      },
      {
        name: 'Main Stone',
        namespace: 'custom',
        key: 'main_stone_jewelry',
        type: 'single_line_text_field',
        description: 'Primary gemstone or stone type'
      },
      {
        name: 'Metal Purity',
        namespace: 'custom',
        key: 'metal_purity_jewelry',
        type: 'single_line_text_field',
        description: 'Purity of metal (e.g., 14k, 18k, 925)'
      },
      {
        name: 'Metal Type',
        namespace: 'custom',
        key: 'metal_jewelry',
        type: 'single_line_text_field',
        description: 'Type of metal (Gold, Silver, Platinum, etc.)'
      },
      {
        name: 'Brand',
        namespace: 'custom',
        key: 'brand_jewelry',
        type: 'single_line_text_field',
        description: 'Brand or designer name'
      }
    ];

    // Dynamically ensure definitions for any auto-created custom metafields
    // (e.g. eBay required specifics mapped to custom.<slug>) that aren't already
    // covered by the standard list above. Only the 'custom' namespace is auto-created;
    // 'shopify' is a reserved taxonomy namespace and must not be defined by us.
    if (extraKeys && extraKeys.length > 0) {
      const known = new Set(definitions.map(d => `${d.namespace}.${d.key}`));
      const humanize = (key: string) =>
        key.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      for (const ek of extraKeys) {
        if (ek.namespace !== 'custom') continue;
        const fullKey = `${ek.namespace}.${ek.key}`;
        if (known.has(fullKey)) continue;
        known.add(fullKey);
        definitions.push({
          name: humanize(ek.key),
          namespace: ek.namespace,
          key: ek.key,
          type: ek.type || 'single_line_text_field',
          description: 'eBay item specific'
        });
      }
    }
    
    for (const def of definitions) {
      try {
        await this.createMetafieldDefinition(def);
      } catch (error) {
        console.error(`Failed to create definition for ${def.namespace}.${def.key}:`, error);
        // Continue with other definitions even if one fails
      }
    }
    
    console.log('✅ Metafield definitions check complete');
  }

  /**
   * @deprecated Use setProductMetafields() instead
   * Old REST API method - kept for backwards compatibility
   */
  async createProductMetafield(productId: string, metafield: { namespace: string; key: string; value: string; type: string }): Promise<void> {
    console.warn('⚠️  Using deprecated createProductMetafield - consider using setProductMetafields instead');
    try {
      await this.request(`/products/${productId}/metafields.json`, {
        method: 'POST',
        body: JSON.stringify({
          metafield: {
            namespace: metafield.namespace,
            key: metafield.key,
            value: metafield.value,
            type: metafield.type
          }
        })
      });
    } catch (error) {
      console.error(`Failed to create metafield ${metafield.namespace}.${metafield.key}:`, error);
      throw error;
    }
  }

  async updateProduct(productId: string, productData: Partial<ShopifyProduct>): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/products/${productId}.json`, {
        method: 'PUT',
        headers: this.headers,
        body: JSON.stringify({ product: productData })
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.errors || 'Failed to update product');
      }

      return responseData;
    } catch (error) {
      console.error('Error in updateProduct:', error);
      throw error;
    }
  }

  async getProduct(productId: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/products/${productId}.json`, {
        method: 'GET',
        headers: this.headers
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.errors || 'Failed to fetch product');
      }

      return responseData;
    } catch (error) {
      console.error('Error in getProduct:', error);
      throw error;
    }
  }

  async getProducts(limit = 50, page = 1): Promise<any> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      page: page.toString(),
      fields: 'id,title,body_html,vendor,product_type,status,tags,variants,images,metafields'
    });
    return this.request(`/products.json?${params.toString()}`);
  }

  /**
   * Run a raw GraphQL query against the Shopify Admin API via the proxy.
   * Returns the parsed { data, errors } response. `variables` are forwarded
   * to Shopify as-is.
   */
  async graphqlRequest(query: string, variables?: Record<string, any>): Promise<any> {
    return this.request('/graphql.json', {
      method: 'POST',
      body: JSON.stringify(variables ? { query, variables } : { query })
    });
  }

  /**
   * Fetch all products (paginated) via GraphQL cursor pagination.
   * Returns flattened variant-level rows for inventory comparison.
   */
  async getAllProductVariantsGraphQL(
    onPage?: (loadedProducts: number) => void
  ): Promise<Array<{
    productId: string;
    variantId: string;
    sku: string;
    title: string;
    price: number;
    quantity: number;
    status: string;
    vendor: string;
    productType: string;
    imageUrl?: string;
  }>> {
    const rows: Array<any> = [];
    let after: string | null = null;
    let hasNext = true;
    let productCount = 0;

    const query = `
      query ($first: Int!, $after: String) {
        products(first: $first, after: $after) {
          pageInfo { hasNextPage endCursor }
          edges {
            node {
              id
              title
              status
              vendor
              productType
              featuredImage { url }
              variants(first: 25) {
                edges {
                  node { id sku price inventoryQuantity }
                }
              }
            }
          }
        }
      }
    `;

    while (hasNext) {
      const response = await this.graphqlRequest(query, { first: 100, after });

      if (response?.errors?.length) {
        throw new Error(`Shopify GraphQL error: ${JSON.stringify(response.errors)}`);
      }

      const productsConn = response?.data?.products;
      const edges = productsConn?.edges || [];

      for (const edge of edges) {
        const product = edge.node;
        productCount++;
        for (const variantEdge of product.variants?.edges || []) {
          const variant = variantEdge.node;
          if (!variant.sku) continue;
          rows.push({
            productId: product.id,
            variantId: variant.id,
            sku: variant.sku,
            title: product.title,
            price: parseFloat(variant.price) || 0,
            quantity: variant.inventoryQuantity || 0,
            status: (product.status || '').toLowerCase(),
            vendor: product.vendor || '',
            productType: product.productType || '',
            imageUrl: product.featuredImage?.url
          });
        }
      }

      if (onPage) onPage(productCount);

      hasNext = productsConn?.pageInfo?.hasNextPage || false;
      after = productsConn?.pageInfo?.endCursor || null;
      if (!after) hasNext = false;

      if (hasNext) await new Promise(resolve => setTimeout(resolve, 150));
    }

    return rows;
  }

  async createMetafield(productId: string, metafield: {
    key: string;
    value: string;
    type: string;
    namespace: string;
  }): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/products/${productId}/metafields.json`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({ metafield })
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.errors || 'Failed to create metafield');
      }

      return responseData;
    } catch (error) {
      console.error('Error in createMetafield:', error);
      throw error;
    }
  }

  async getProductMetafields(productId: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/products/${productId}/metafields.json`, {
        method: 'GET',
        headers: this.headers
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.errors || 'Failed to fetch metafields');
      }

      return responseData;
    } catch (error) {
      console.error('Error in getProductMetafields:', error);
      throw error;
    }
  }

  // Get metafield definitions for a specific product type
  async getMetafieldDefinitions(ownerType: string = 'PRODUCT'): Promise<any[]> {
    try {
      // Use GraphQL to fetch metafield definitions
      const query = `
        query {
          metafieldDefinitions(first: 50, ownerType: ${ownerType}) {
            edges {
              node {
                id
                name
                namespace
                key
                description
                type {
                  name
                }
                validations {
                  name
                  value
                }
              }
            }
          }
        }
      `;

      const response = await fetch(`${this.baseUrl}/graphql.json`, {
        method: 'POST',
        headers: {
          ...this.headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query })
      });

      const responseData = await response.json();
      
      if (!response.ok || responseData.errors) {
        throw new Error(responseData.errors?.[0]?.message || 'Failed to fetch metafield definitions');
      }

      return responseData.data?.metafieldDefinitions?.edges?.map((edge: any) => edge.node) || [];
    } catch (error) {
      console.error('Error fetching metafield definitions:', error);
      throw error;
    }
  }

  // Get metafield definitions filtered by product type (using namespace or custom logic)
  async getMetafieldDefinitionsForProductType(productType: string): Promise<any[]> {
    try {
      const allDefinitions = await this.getMetafieldDefinitions('PRODUCT');
      
      // Filter metafields based on product type
      // You can customize this logic based on how your metafields are organized
      // For now, we'll return all product metafields and let the form handle filtering
      return allDefinitions;
    } catch (error) {
      console.error('Error fetching metafield definitions for product type:', error);
      throw error;
    }
  }

  // Find Shopify taxonomy category by product type name
  async findTaxonomyCategory(searchTerm: string): Promise<string | null> {
    try {
      const query = `
        query FindCategory($search: String!) {
          taxonomy {
            categories(first: 10, search: $search) {
              nodes {
                id
                fullName
                isLeaf
              }
            }
          }
        }
      `;

      const response = await fetch(`${this.baseUrl}/graphql.json`, {
        method: 'POST',
        headers: {
          ...this.headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          query,
          variables: { search: searchTerm }
        })
      });

      const responseData = await response.json();
      
      if (!response.ok || responseData.errors) {
        console.error('Error finding taxonomy category:', responseData.errors);
        return null;
      }

      const categories = responseData.data?.taxonomy?.categories?.nodes || [];
      
      // Prefer leaf categories (most specific)
      const leafCategory = categories.find((cat: any) => cat.isLeaf);
      if (leafCategory) {
        console.log(`✅ Found taxonomy category for "${searchTerm}": ${leafCategory.fullName} (${leafCategory.id})`);
        return leafCategory.id;
      }

      // Fall back to first category if no leaf found
      if (categories.length > 0) {
        console.log(`✅ Found taxonomy category for "${searchTerm}": ${categories[0].fullName} (${categories[0].id})`);
        return categories[0].id;
      }

      console.warn(`⚠️ No taxonomy category found for "${searchTerm}"`);
      return null;
    } catch (error) {
      console.error('Error finding taxonomy category:', error);
      return null;
    }
  }

  // Update product category via GraphQL
  async updateProductCategory(productId: string, categoryId: string): Promise<boolean> {
    try {
      const normalizedCategoryId = normalizeCategoryGid(categoryId) || categoryId;
      // Convert numeric ID to GraphQL GID format if needed
      const productGid = productId.startsWith('gid://') 
        ? productId 
        : `gid://shopify/Product/${productId}`;

      const mutation = `
        mutation SetProductCategory($id: ID!, $categoryId: ID!) {
          productUpdate(input: { id: $id, category: $categoryId }) {
            product {
              id
              category {
                id
                fullName
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const response = await fetch(`${this.baseUrl}/graphql.json`, {
        method: 'POST',
        headers: {
          ...this.headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          mutation,
          variables: { 
            id: productGid,
            categoryId: normalizedCategoryId
          }
        })
      });

      const responseData = await response.json();
      
      if (!response.ok || responseData.errors) {
        console.error('❌ GraphQL error updating product category:', responseData.errors);
        return false;
      }

      if (responseData.data?.productUpdate?.userErrors?.length > 0) {
        console.error('❌ User errors updating product category:', responseData.data.productUpdate.userErrors);
        return false;
      }

      const updatedCategory = responseData.data?.productUpdate?.product?.category;
      if (updatedCategory) {
        console.log(`✅ Product category updated: ${updatedCategory.fullName} (${updatedCategory.id})`);
        return true;
      }

      return false;
    } catch (error) {
      console.error('❌ Error updating product category:', error);
      return false;
    }
  }

  async getLocations(): Promise<ShopifyLocation[]> {
    try {
      const response = await this.request<{ locations: ShopifyLocation[] }>('/locations.json');
      return response.locations || [];
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  }

  async getOrdersForCurrentMonth(locationId?: string): Promise<ShopifyOrder[]> {
    try {
      const now = new Date();
      // TEMPORARY: Get last 3 months instead of just current month to debug
      const startOfMonth = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

      const params = new URLSearchParams({
        status: 'any',
        created_at_min: startOfMonth.toISOString(),
        created_at_max: endOfMonth.toISOString(),
        limit: '250'
      });

      if (locationId) {
        params.append('location_id', locationId);
      }

      const response = await this.request<{ orders: ShopifyOrder[] }>(`/orders.json?${params}`);
      return response.orders || [];
    } catch (error) {
      console.error('Error fetching orders for current month:', error);
      throw error;
    }
  }

  async getOrdersForPreviousMonth(locationId?: string): Promise<ShopifyOrder[]> {
    try {
      const now = new Date();
      const startOfPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endOfPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);

      const params = new URLSearchParams({
        status: 'any',
        created_at_min: startOfPrevMonth.toISOString(),
        created_at_max: endOfPrevMonth.toISOString(),
        limit: '250'
      });

      if (locationId) {
        params.append('location_id', locationId);
      }

      const response = await this.request<{ orders: ShopifyOrder[] }>(`/orders.json?${params}`);
      return response.orders || [];
    } catch (error) {
      console.error('Error fetching orders for previous month:', error);
      throw error;
    }
  }

  async getOrderByNumber(orderNumber: string): Promise<ShopifyOrder | null> {
    try {
      const params = new URLSearchParams({
        name: `#${orderNumber}`,
        status: 'any',
        limit: '1'
      });
      const response = await this.request<{ orders: ShopifyOrder[] }>(`/orders.json?${params}`);
      const orders = response.orders || [];
      return orders.length > 0 ? orders[0] : null;
    } catch (error) {
      console.error('Error fetching order by number:', error);
      return null;
    }
  }

  async getAllRecentOrders(): Promise<ShopifyOrder[]> {
    try {
      let allOrders: ShopifyOrder[] = [];
      let sinceId: string | null = null;
      const maxRequests = 100; // Allow up to 100 requests to get more orders
      let requestCount = 0;
      
      console.log('🔄 Starting to fetch ALL orders from Shopify...');
      
      // Use since_id pagination which works better for getting all orders
      while (requestCount < maxRequests) {
        try {
          const params = new URLSearchParams({
            status: 'any',
            limit: '250',
            order: 'created_at desc'
          });
          
          if (sinceId) {
            params.append('since_id', sinceId);
          }

          const response = await this.request<{ orders: ShopifyOrder[] }>(`/orders.json?${params}`);
          const orders = response.orders || [];
          
          console.log(`📦 Fetched batch ${requestCount + 1}: ${orders.length} orders`);
          
          if (orders.length === 0) {
            console.log('✅ No more orders to fetch');
            break;
          }
          
          allOrders = allOrders.concat(orders);
          
          // If we got less than the limit, we've reached the end
          if (orders.length < 250) {
            console.log('✅ Reached end of orders (got less than 250)');
            break;
          }
          
          // Use the last order's ID for the next request
          const lastOrder = orders[orders.length - 1];
          sinceId = lastOrder.id;
          
          requestCount++;
          
          // Add a small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
          console.error(`❌ Error in batch ${requestCount + 1}:`, error);
          break;
        }
      }
      
      console.log(`🎉 Successfully fetched ${allOrders.length} total orders from Shopify`);
      
      // If we didn't get many orders, try the date range approach as fallback
      if (allOrders.length < 100) {
        console.log('⚠️ Low order count, trying fallback date range method...');
        const fallbackOrders = await this.getAllOrdersByDateRanges();
        
        // Merge and deduplicate
        const combined = [...allOrders, ...fallbackOrders];
        const uniqueOrders = combined.filter((order, index, self) => 
          index === self.findIndex(o => o.id === order.id)
        );
        
        uniqueOrders.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        console.log(`✨ Final result: ${uniqueOrders.length} unique orders after fallback`);
        return uniqueOrders;
      }
      
      // Remove duplicates and sort
      const uniqueOrders = allOrders.filter((order, index, self) => 
        index === self.findIndex(o => o.id === order.id)
      );
      
      uniqueOrders.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      console.log(`✨ Final result: ${uniqueOrders.length} unique orders`);
      return uniqueOrders;
    } catch (error) {
      console.error('Error fetching recent orders:', error);
      throw error;
    }
  }

  private async getAllOrdersByDateRanges(): Promise<ShopifyOrder[]> {
    const allOrders: ShopifyOrder[] = [];
    const now = new Date();
    
    // Create date ranges going back 2 years
    const dateRanges = [];
    for (let i = 0; i < 24; i++) { // 24 months = 2 years
      const endDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const startDate = new Date(now.getFullYear(), now.getMonth() - i - 1, 1);
      dateRanges.push({ start: startDate, end: endDate });
    }

    // Fetch orders for each month
    for (const range of dateRanges) {
      try {
        const params = new URLSearchParams({
          status: 'any',
          created_at_min: range.start.toISOString(),
          created_at_max: range.end.toISOString(),
          limit: '250'
        });

        const response = await this.request<{ orders: ShopifyOrder[] }>(`/orders.json?${params}`);
        const orders = response.orders || [];
        
        if (orders.length > 0) {
          allOrders.push(...orders);
        }
      } catch (error) {
        // Continue with other ranges if one fails
        continue;
      }
    }

    return allOrders;
  }

  async getTopSalesRepFromCommissions(year?: number, month?: number): Promise<{name: string, totalSales: number} | null> {
    try {
      console.log('Fetching top sales rep from commissions...');
      
      // Import commissions service
      const { fetchCommissions } = await import('./commissions');
      const allCommissions = await fetchCommissions();
      
      // Filter out affiliate orders from sales rep rankings
      const commissions = allCommissions.filter(commission => !commission.isAffiliateOrder);
      
      // Get current month date range
      // If year and month are provided, use those; otherwise use current month
      const now = new Date();
      const targetYear = year !== undefined ? year : now.getFullYear();
      const targetMonth = month !== undefined ? month : now.getMonth();
      
      const currentMonthStart = new Date(targetYear, targetMonth, 1);
      const currentMonthEnd = new Date(targetYear, targetMonth + 1, 0);
      
      console.log('Date filtering for top sales rep:', {
        now: now.toISOString(),
        currentMonthStart: currentMonthStart.toISOString(),
        currentMonthEnd: currentMonthEnd.toISOString(),
        totalCommissions: commissions.length
      });
      
      // Helper function to parse dates in M/D/YYYY format
      const parseCommissionDate = (dateStr: string): Date | null => {
        if (!dateStr) return null;
        
        // Try different date formats
        let parsedDate: Date;
        
        // Handle M/D/YYYY format (like "8/1/2025")
        if (dateStr.includes('/')) {
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const month = parseInt(parts[0]) - 1; // Month is 0-based
            const day = parseInt(parts[1]);
            const year = parseInt(parts[2]);
            parsedDate = new Date(year, month, day);
          } else {
            parsedDate = new Date(dateStr);
          }
        }
        // Handle YYYY-MM-DD format
        else if (dateStr.includes('-')) {
          parsedDate = new Date(dateStr);
        }
        // Handle other formats
        else {
          parsedDate = new Date(dateStr);
        }
        
        // Validate the parsed date
        if (isNaN(parsedDate.getTime())) {
          return null;
        }
        
        return parsedDate;
      };

      // Filter commissions for current month
      const currentMonthCommissions = commissions.filter(commission => {
        try {
          const commissionDate = parseCommissionDate(commission.date);
          if (!commissionDate) {
            console.log('Could not parse commission date:', commission.date);
            return false;
          }
          
          const isInRange = commissionDate >= currentMonthStart && commissionDate <= currentMonthEnd;
          console.log('Commission date check:', {
            date: commission.date,
            parsedDate: commissionDate.toISOString(),
            isInRange,
            total: commission.total,
            user: commission.user
          });
          return isInRange;
        } catch (error) {
          console.log('Failed to parse commission date:', commission.date, error);
          return false;
        }
      });
      
      // Aggregate sales by sales rep
      const salesRepMap = new Map<string, number>();
      
      currentMonthCommissions.forEach(commission => {
        const repName = commission.user?.trim() || commission.email?.split('@')[0] || 'Unknown';
        console.log('Processing commission for top rep:', {
          date: commission.date,
          user: commission.user,
          email: commission.email,
          repName: repName,
          total: commission.total
        });
        
        if (repName && repName !== 'Unknown') {
          const currentTotal = salesRepMap.get(repName) || 0;
          const newTotal = currentTotal + (commission.total || 0);
          salesRepMap.set(repName, newTotal);
          console.log(`Updated rep "${repName}": $${newTotal}`);
        }
      });
      
      // Find the top sales rep
      let topRep = null;
      let highestSales = 0;
      
      console.log('Final sales rep totals:');
      salesRepMap.forEach((totalSales, repName) => {
        console.log(`  ${repName}: $${totalSales}`);
        if (totalSales > highestSales) {
          highestSales = totalSales;
          topRep = { name: repName, totalSales };
        }
      });
      
      console.log('Selected top sales rep:', topRep);
      console.log(`Processed ${currentMonthCommissions.length} commissions for current month`);
      return topRep;
      
    } catch (error) {
      console.error('Error getting top sales rep from commissions:', error);
      return null;
    }
  }

  async getSalesDataFromCommissions(year?: number, month?: number): Promise<LocationSalesData[]> {
    try {
      console.log('Fetching sales data from commissions...');
      
      // Import commissions service
      const { fetchCommissions } = await import('./commissions');
      const allCommissions = await fetchCommissions();
      
      // Filter out affiliate orders from dashboard totals
      const commissions = allCommissions.filter(commission => !commission.isAffiliateOrder);
      
      // Get current month and previous month date ranges
      // If year and month are provided, use those; otherwise use current month
      const now = new Date();
      const targetYear = year !== undefined ? year : now.getFullYear();
      const targetMonth = month !== undefined ? month : now.getMonth();
      
      const currentMonthStart = new Date(targetYear, targetMonth, 1);
      const currentMonthEnd = new Date(targetYear, targetMonth + 1, 0);
      const previousMonthStart = new Date(targetYear, targetMonth - 1, 1);
      const previousMonthEnd = new Date(targetYear, targetMonth, 0);
      
      console.log('Date filtering for sales data:', {
        now: now.toISOString(),
        currentMonthStart: currentMonthStart.toISOString(),
        currentMonthEnd: currentMonthEnd.toISOString(),
        previousMonthStart: previousMonthStart.toISOString(),
        previousMonthEnd: previousMonthEnd.toISOString(),
        totalCommissions: commissions.length
      });
      
      // Helper function to parse dates in M/D/YYYY format
      const parseCommissionDate = (dateStr: string): Date | null => {
        if (!dateStr) return null;
        
        // Try different date formats
        let parsedDate: Date;
        
        // Handle M/D/YYYY format (like "8/1/2025")
        if (dateStr.includes('/')) {
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const month = parseInt(parts[0]) - 1; // Month is 0-based
            const day = parseInt(parts[1]);
            const year = parseInt(parts[2]);
            parsedDate = new Date(year, month, day);
          } else {
            parsedDate = new Date(dateStr);
          }
        }
        // Handle YYYY-MM-DD format
        else if (dateStr.includes('-')) {
          parsedDate = new Date(dateStr);
        }
        // Handle other formats
        else {
          parsedDate = new Date(dateStr);
        }
        
        // Validate the parsed date
        if (isNaN(parsedDate.getTime())) {
          return null;
        }
        
        return parsedDate;
      };

      // Filter commissions by month
      const currentMonthCommissions = commissions.filter(commission => {
        try {
          const commissionDate = parseCommissionDate(commission.date);
          if (!commissionDate) {
            console.log('Could not parse commission date for sales data:', commission.date);
            return false;
          }
          
          const isInRange = commissionDate >= currentMonthStart && commissionDate <= currentMonthEnd;
          console.log('Sales data commission check:', {
            date: commission.date,
            parsedDate: commissionDate.toISOString(),
            isInRange,
            total: commission.total,
            store: commission.store,
            user: commission.user
          });
          return isInRange;
        } catch (error) {
          console.log('Failed to parse commission date for sales data:', commission.date, error);
          return false;
        }
      });
      
      const previousMonthCommissions = commissions.filter(commission => {
        try {
          const commissionDate = parseCommissionDate(commission.date);
          if (!commissionDate) return false;
          return commissionDate >= previousMonthStart && commissionDate <= previousMonthEnd;
        } catch {
          return false;
        }
      });
      
      console.log('Current month commissions:', currentMonthCommissions.length);
      console.log('Previous month commissions:', previousMonthCommissions.length);
      
      // Create a map to aggregate sales by store
      const locationSalesMap = new Map<string, {
        locationId: string;
        locationName: string;
        totalSales: number;
        transactionCount: number;
        previousMonthSales: number;
        status: 'online' | 'offline';
      }>();
      
      // Initialize all known Chorost locations
      Object.entries(CHOROST_LOCATIONS).forEach(([locationId, locationName]) => {
        locationSalesMap.set(locationName, {
          locationId,
          locationName,
          totalSales: 0,
          transactionCount: 0,
          previousMonthSales: 0,
          status: 'online'
        });
      });
      
      // Process current month commissions
      currentMonthCommissions.forEach(commission => {
        const storeName = commission.store?.trim();
        console.log('Processing commission:', {
          date: commission.date,
          store: storeName,
          total: commission.total,
          user: commission.user
        });
        
        if (!storeName) {
          console.log('Skipping commission with no store name');
          return;
        }
        
        // Try to find matching location by name
        let locationData = locationSalesMap.get(storeName);
        if (!locationData) {
          // Try partial matching for common variations
          for (const [name, data] of locationSalesMap.entries()) {
            if (name.toLowerCase().includes(storeName.toLowerCase()) || 
                storeName.toLowerCase().includes(name.toLowerCase())) {
              locationData = data;
              console.log(`Matched store "${storeName}" to location "${name}" via partial matching`);
              break;
            }
          }
        } else {
          console.log(`Matched store "${storeName}" to existing location`);
        }
        
        // If still no match, create a new entry
        if (!locationData) {
          console.log(`Creating new location entry for store: "${storeName}"`);
          locationData = {
            locationId: 'unknown',
            locationName: storeName,
            totalSales: 0,
            transactionCount: 0,
            previousMonthSales: 0,
            status: 'online' as const
          };
          locationSalesMap.set(storeName, locationData);
        }
        
        locationData.totalSales += commission.total || 0;
        locationData.transactionCount += 1;
        console.log(`Updated location "${locationData.locationName}": $${locationData.totalSales} (${locationData.transactionCount} transactions)`);
      });
      
      // Process previous month commissions
      previousMonthCommissions.forEach(commission => {
        const storeName = commission.store?.trim();
        if (!storeName) return;
        
        let locationData = locationSalesMap.get(storeName);
        if (!locationData) {
          // Try partial matching
          for (const [name, data] of locationSalesMap.entries()) {
            if (name.toLowerCase().includes(storeName.toLowerCase()) || 
                storeName.toLowerCase().includes(name.toLowerCase())) {
              locationData = data;
              break;
            }
          }
        }
        
        if (locationData) {
          locationData.previousMonthSales += commission.total || 0;
        }
      });
      
      // Convert to final format
      const locationSalesArray: LocationSalesData[] = [];
      
      locationSalesMap.forEach(locationData => {
        // Calculate growth percentage
        let growthPercent = 0;
        if (locationData.previousMonthSales > 0) {
          growthPercent = ((locationData.totalSales - locationData.previousMonthSales) / locationData.previousMonthSales) * 100;
        } else if (locationData.totalSales > 0) {
          growthPercent = 100; // New sales where there were none before
        }

        const finalData: LocationSalesData = {
          locationId: locationData.locationId,
          locationName: locationData.locationName,
          totalSales: locationData.totalSales,
          transactionCount: locationData.transactionCount,
          averageTicket: locationData.transactionCount > 0 ? locationData.totalSales / locationData.transactionCount : 0,
          previousMonthSales: locationData.previousMonthSales,
          growthPercent: Math.round(growthPercent * 10) / 10, // Round to 1 decimal place
          status: locationData.status
        };

        locationSalesArray.push(finalData);
      });

      // Sort by total sales (highest to lowest)
      locationSalesArray.sort((a, b) => b.totalSales - a.totalSales);

      console.log('Final commission-based sales data:', locationSalesArray.map(l => ({
        name: l.locationName,
        sales: l.totalSales,
        transactions: l.transactionCount
      })));
      
      return locationSalesArray;

    } catch (error) {
      console.error('Error getting sales data from commissions:', error);
      throw error;
    }
  }

  async getPOSSalesDataByLocation(): Promise<LocationSalesData[]> {
    try {
      console.log('Fetching POS sales data by location...');
      
      // Get current and previous month orders
      const [currentMonthOrders, previousMonthOrders] = await Promise.all([
        this.getOrdersForCurrentMonth(),
        this.getOrdersForPreviousMonth()
      ]);
      
      console.log('Found orders for current month:', currentMonthOrders.length);
      console.log('Found orders for previous month:', previousMonthOrders.length);

      // Filter for POS orders - only include orders from known Chorost locations
      const filterPOSOrders = (orders: ShopifyOrder[]) => {
        const filtered = orders.filter(order => {
          // Must have a location_id that matches one of our known Chorost locations
          return order.location_id && CHOROST_LOCATIONS.hasOwnProperty(order.location_id);
        });
        
        return filtered;
      };

      const currentPOSOrders = filterPOSOrders(currentMonthOrders);
      const previousPOSOrders = filterPOSOrders(previousMonthOrders);

      console.log('Current month POS orders:', currentPOSOrders.length);
      console.log('Previous month POS orders:', previousPOSOrders.length);

      const locationSalesMap = new Map<string, {
        locationId: string;
        locationName: string;
        totalSales: number;
        transactionCount: number;
        previousMonthSales: number;
        status: 'online' | 'offline';
      }>();

      // Initialize all Chorost locations using our mapping
      Object.entries(CHOROST_LOCATIONS).forEach(([locationId, locationName]) => {
        locationSalesMap.set(locationId, {
          locationId,
          locationName,
          totalSales: 0,
          transactionCount: 0,
          previousMonthSales: 0,
          status: 'online' // Assume all are online unless we know otherwise
        });
      });

      // Process current month POS orders
      currentPOSOrders.forEach(order => {
        const locationId = order.location_id!; // We know it exists from filtering
        const orderTotal = parseFloat(order.total_price) || 0;
        
        const locationData = locationSalesMap.get(locationId);
        if (locationData) {
          locationData.totalSales += orderTotal;
          locationData.transactionCount += 1;
        }
      });

      // Process previous month POS orders for comparison
      previousPOSOrders.forEach(order => {
        const locationId = order.location_id!; // We know it exists from filtering
        const locationData = locationSalesMap.get(locationId);
        
        if (locationData) {
          const orderTotal = parseFloat(order.total_price) || 0;
          locationData.previousMonthSales += orderTotal;
        }
      });

      // Convert to final format
      const locationSalesArray: LocationSalesData[] = [];
      
      locationSalesMap.forEach(locationData => {
        // Calculate growth percentage
        let growthPercent = 0;
        if (locationData.previousMonthSales > 0) {
          growthPercent = ((locationData.totalSales - locationData.previousMonthSales) / locationData.previousMonthSales) * 100;
        } else if (locationData.totalSales > 0) {
          growthPercent = 100; // New sales where there were none before
        }

        const finalData: LocationSalesData = {
          locationId: locationData.locationId,
          locationName: locationData.locationName,
          totalSales: locationData.totalSales,
          transactionCount: locationData.transactionCount,
          averageTicket: locationData.transactionCount > 0 ? locationData.totalSales / locationData.transactionCount : 0,
          previousMonthSales: locationData.previousMonthSales,
          growthPercent: Math.round(growthPercent * 10) / 10, // Round to 1 decimal place
          status: locationData.status
        };

        locationSalesArray.push(finalData);
      });

      // Sort by total sales (highest to lowest)
      locationSalesArray.sort((a, b) => b.totalSales - a.totalSales);

      console.log('Final processed location sales data:', locationSalesArray.map(l => ({
        name: l.locationName,
        sales: l.totalSales,
        transactions: l.transactionCount
      })));
      
      return locationSalesArray;

    } catch (error) {
      console.error('Error getting POS sales data by location:', error);
      throw error;
    }
  }

  // Draft Order Methods for Custom Jewelry Requests
  
  /**
   * Create a draft order in Shopify
   * @param draftOrderData - The draft order payload
   * @returns The created draft order
   */
  async createDraftOrder(draftOrderData: {
    line_items: Array<{
      title: string;
      price: string;
      quantity: number;
      taxable?: boolean;
    }>;
    customer?: {
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
      default_address?: {
        address1?: string;
        city?: string;
        province?: string;
        zip?: string;
        country?: string;
      };
    };
    billing_address?: {
      first_name?: string;
      last_name?: string;
      address1?: string;
      city?: string;
      province?: string;
      zip?: string;
      country?: string;
      phone?: string;
    };
    shipping_address?: {
      first_name?: string;
      last_name?: string;
      address1?: string;
      city?: string;
      province?: string;
      zip?: string;
      country?: string;
      phone?: string;
    };
    note?: string;
    tags?: string;
    tax_exempt?: boolean;
    /** Shopify location the draft should be created under (POS / admin location). */
    location_id?: string | number;
  }) {
    console.log('Creating Shopify draft order:', JSON.stringify(draftOrderData, null, 2));

    const postDraft = (payload: typeof draftOrderData) =>
      this.request<{ draft_order: any }>('/draft_orders.json', {
        method: 'POST',
        body: JSON.stringify({ draft_order: payload }),
      });

    try {
      const response = await postDraft(draftOrderData);
      console.log('Draft order created successfully:', response);
      return response.draft_order;
    } catch (error) {
      // Shopify may reject undocumented location_id; retry without it so
      // the draft still gets created.
      if (draftOrderData.location_id != null) {
        const { location_id: _ignored, ...withoutLocation } = draftOrderData;
        console.warn('Draft order with location_id failed, retrying without it:', error);
        try {
          const retry = await postDraft(withoutLocation);
          console.log('Draft order created without location_id:', retry);
          return retry.draft_order;
        } catch (retryError) {
          console.error('Failed to create draft order:', retryError);
          throw retryError;
        }
      }
      console.error('Failed to create draft order:', error);
      throw error;
    }
  }

  /**
   * Send an invoice for a draft order to the customer
   * @param draftOrderId - The Shopify draft order ID
   * @param invoiceData - Optional custom invoice details
   * @returns The invoice response
   */
  async sendDraftOrderInvoice(draftOrderId: string, invoiceData?: {
    to?: string;
    from?: string;
    subject?: string;
    custom_message?: string;
    bcc?: string[];
  }) {
    console.log('Sending invoice for draft order:', draftOrderId);
    
    try {
      const response = await this.request<{ draft_order_invoice: any }>(
        `/draft_orders/${draftOrderId}/send_invoice.json`,
        {
          method: 'POST',
          body: JSON.stringify({ draft_order_invoice: invoiceData || {} })
        }
      );
      
      console.log('Invoice sent successfully:', response);
      return response.draft_order_invoice;
    } catch (error) {
      console.error('Failed to send invoice:', error);
      throw error;
    }
  }

  /**
   * Create a draft order and immediately send an invoice
   * Convenience method for custom jewelry requests
   */
  async createDraftOrderAndSendInvoice(params: {
    customerName: string;
    customerEmail: string;
    customerPhone?: string;
    customerAddress?: string;
    customerCity?: string;
    customerState?: string;
    customerZip?: string;
    customerCountry?: string;
    itemDescription: string;
    price: string;
    jobId: string;
    notes?: string;
  }) {
    // Split name into first and last
    const nameParts = params.customerName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    // Create the draft order
    const draftOrder = await this.createDraftOrder({
      line_items: [{
        title: `Custom Jewelry - ${params.itemDescription}`,
        price: params.price,
        quantity: 1,
        taxable: true
      }],
      customer: {
        first_name: firstName,
        last_name: lastName,
        email: params.customerEmail,
        phone: params.customerPhone,
        default_address: params.customerAddress ? {
          address1: params.customerAddress,
          city: params.customerCity,
          province: params.customerState,
          zip: params.customerZip,
          country: params.customerCountry || 'US'
        } : undefined
      },
      billing_address: params.customerAddress ? {
        first_name: firstName,
        last_name: lastName,
        address1: params.customerAddress,
        city: params.customerCity,
        province: params.customerState,
        zip: params.customerZip,
        country: params.customerCountry || 'US',
        phone: params.customerPhone
      } : undefined,
      note: `Custom Jewelry Request - Job ID: ${params.jobId}\n${params.notes || ''}`,
      tags: `custom-jewelry, ${params.jobId}`
    });

    // Shopify needs time to finish calculating the draft order before we can
    // send the invoice. Retry up to 4 times with increasing delays.
    let invoice: any;
    const delays = [1500, 3000, 5000, 8000];
    for (let attempt = 0; attempt < delays.length; attempt++) {
      await new Promise((r) => setTimeout(r, delays[attempt]));
      try {
        invoice = await this.sendDraftOrderInvoice(draftOrder.id, {
          to: params.customerEmail,
          subject: `Invoice for Your Custom Jewelry Order - ${params.jobId}`,
          custom_message: `Thank you for your custom jewelry order with Chorost & Co.! Please review and complete payment for your order.\n\nJob ID: ${params.jobId}`,
        });
        break;
      } catch (err: any) {
        const msg = typeof err?.message === 'string' ? err.message : JSON.stringify(err);
        const isCalculating = msg.toLowerCase().includes('calculating');
        if (!isCalculating || attempt === delays.length - 1) throw err;
        console.warn(`Draft order still calculating, retrying (${attempt + 1}/${delays.length})…`);
      }
    }

    return {
      draftOrder,
      invoice
    };
  }

  /**
   * Get a draft order by ID
   */
  async getDraftOrder(draftOrderId: string) {
    try {
      const response = await this.request<{ draft_order: any }>(
        `/draft_orders/${draftOrderId}.json`,
        { method: 'GET' }
      );
      return response.draft_order;
    } catch (error) {
      console.error('Failed to get draft order:', error);
      throw error;
    }
  }

  /**
   * Update a draft order
   */
  async updateDraftOrder(draftOrderId: string, updateData: any) {
    try {
      const response = await this.request<{ draft_order: any }>(
        `/draft_orders/${draftOrderId}.json`,
        {
          method: 'PUT',
          body: JSON.stringify({ draft_order: updateData })
        }
      );
      return response.draft_order;
    } catch (error) {
      console.error('Failed to update draft order:', error);
      throw error;
    }
  }

  /**
   * Delete a draft order
   */
  async deleteDraftOrder(draftOrderId: string) {
    try {
      await this.request(`/draft_orders/${draftOrderId}.json`, {
        method: 'DELETE'
      });
      console.log('Draft order deleted:', draftOrderId);
      return true;
    } catch (error) {
      console.error('Failed to delete draft order:', error);
      throw error;
    }
  }

  /**
   * Complete a draft order (convert to a real order)
   */
  async completeDraftOrder(draftOrderId: string, paymentPending: boolean = false) {
    try {
      const response = await this.request<{ draft_order: any }>(
        `/draft_orders/${draftOrderId}/complete.json${paymentPending ? '?payment_pending=true' : ''}`,
        { method: 'PUT' }
      );
      console.log('Draft order completed:', response);
      return response.draft_order;
    } catch (error) {
      console.error('Failed to complete draft order:', error);
      throw error;
    }
  }

  async testConnection() {
    try {
      // Try to fetch shop information as a test
      const response = await this.request('/shop.json', {
        method: 'GET'
      });
      console.log('Connection test successful:', response);
      return response;
    } catch (error) {
      console.error('Connection test failed:', error);
      throw error;
    }
  }
}

// Singleton instance
let shopifyService: ShopifyService | null = null;

export const getShopifyService = (): ShopifyService => {
  if (!shopifyService) {
    shopifyService = new ShopifyService();
  }
  return shopifyService;
};

export async function getShopifyTypes(): Promise<ShopifyType[]> {
  const db = getFirestore();
  const typesRef = doc(db, 'shopify', 'types');
  const typesDoc = await getDoc(typesRef);
  
  if (!typesDoc.exists()) {
    // If no types exist in Firestore, return default types
    return [
      {
        id: 'ring',
        name: 'Ring',
        fields: [
          {
            id: 'ring_size',
            name: 'Ring Size',
            type: 'select',
            required: true,
            options: ['3', '3.5', '4', '4.5', '5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12'],
            metafield: {
              namespace: 'custom',
              key: 'ring_size',
              type: 'single_line_text_field'
            }
          },
          {
            id: 'resizable',
            name: 'Resizable',
            type: 'boolean',
            required: true,
            metafield: {
              namespace: 'custom',
              key: 'resizable',
              type: 'boolean'
            }
          }
        ],
        tags: ['ring', 'jewelry'],
        collections: ['rings']
      },
      {
        id: 'necklace',
        name: 'Necklace',
        fields: [
          {
            id: 'length',
            name: 'Length',
            type: 'select',
            required: true,
            options: ['14"', '16"', '18"', '20"', '22"', '24"', '26"', '28"', '30"'],
            metafield: {
              namespace: 'custom',
              key: 'length',
              type: 'single_line_text_field'
            }
          },
          {
            id: 'clasp_type',
            name: 'Clasp Type',
            type: 'select',
            required: true,
            options: ['Lobster', 'Spring Ring', 'Toggle', 'Magnetic', 'Box', 'Hook and Eye'],
            metafield: {
              namespace: 'custom',
              key: 'clasp_type',
              type: 'single_line_text_field'
            }
          }
        ],
        tags: ['necklace', 'jewelry'],
        collections: ['necklaces']
      },
      {
        id: 'bracelet',
        name: 'Bracelet',
        fields: [
          {
            id: 'length',
            name: 'Length',
            type: 'select',
            required: true,
            options: ['6"', '6.5"', '7"', '7.5"', '8"', '8.5"'],
            metafield: {
              namespace: 'custom',
              key: 'length',
              type: 'single_line_text_field'
            }
          },
          {
            id: 'clasp_type',
            name: 'Clasp Type',
            type: 'select',
            required: true,
            options: ['Lobster', 'Spring Ring', 'Toggle', 'Magnetic', 'Box', 'Hook and Eye'],
            metafield: {
              namespace: 'custom',
              key: 'clasp_type',
              type: 'single_line_text_field'
            }
          }
        ],
        tags: ['bracelet', 'jewelry'],
        collections: ['bracelets']
      },
      {
        id: 'earrings',
        name: 'Earrings',
        fields: [
          {
            id: 'closure_type',
            name: 'Closure Type',
            type: 'select',
            required: true,
            options: ['Post', 'Hoop', 'Clip-on', 'Screw-back', 'Lever-back'],
            metafield: {
              namespace: 'custom',
              key: 'closure_type',
              type: 'single_line_text_field'
            }
          }
        ],
        tags: ['earrings', 'jewelry'],
        collections: ['earrings']
      }
    ];
  }
  
  return typesDoc.data().types as ShopifyType[];
}

export async function getShopifyType(typeId: string): Promise<ShopifyType | null> {
  const types = await getShopifyTypes();
  return types.find(type => type.id === typeId) || null;
}

export function getDefaultTags(type: ShopifyType): string[] {
  return type.tags;
}

  export function getDefaultCollections(type: ShopifyType): string[] {
    return type.collections;
  }